import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";

const STAGES = ["NEW_LEAD", "CONTACTED", "QUALIFIED", "RFQ", "QUOTE", "NEGOTIATION", "WON", "LOST"];
const STAGE_COLORS = {
  NEW_LEAD: "bg-blue-100 text-blue-700", CONTACTED: "bg-cyan-100 text-cyan-700", QUALIFIED: "bg-purple-100 text-purple-700",
  RFQ: "bg-orange-100 text-orange-700", QUOTE: "bg-indigo-100 text-indigo-700", NEGOTIATION: "bg-amber-100 text-amber-700",
  WON: "bg-green-100 text-green-700", LOST: "bg-red-100 text-red-600",
};
const EMPTY = { name: "", company: "", email: "", phone: "", industry: "", stage: "NEW_LEAD", value: "", notes: "" };

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [stage, setStage] = useState("");
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    setError(false);
    api.get("/admin/leads", { params: { stage: stage || undefined } })
      .then((r) => setLeads(r.data))
      .catch(() => { setLeads([]); setError(true); });
  }, [stage]);
  useEffect(() => { load(); }, [load]);

  const h = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addLead = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      await api.post("/admin/leads", form);
      toast.success("Lead added"); setForm(EMPTY); setShowForm(false); load();
    } catch { toast.error("Failed to add lead"); }
  };

  const setLeadStage = async (id, newStage) => {
    try { await api.patch(`/admin/leads/${id}`, { stage: newStage }); load(); }
    catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/leads/${id}`); toast.success("Lead deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-crm-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">CRM — Sales Pipeline</h1>
        <button data-testid="add-lead-button" onClick={() => setShowForm(!showForm)} className="btn-primary !py-2.5 !px-4 text-[13px]"><Plus size={15} /> Add Lead</button>
      </div>

      <select data-testid="crm-stage-filter" className="pg-input !py-2.5 w-[200px] mt-5" value={stage} onChange={(e) => setStage(e.target.value)}>
        <option value="">All stages</option>
        {STAGES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>

      {showForm && (
        <div className="mt-5 bg-white rounded-xl border border-[var(--pg-accent)]/40 p-6" data-testid="lead-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="pg-label">Name *</label><input data-testid="lead-name-input" className="pg-input !py-2.5" value={form.name} onChange={h("name")} /></div>
            <div><label className="pg-label">Company</label><input data-testid="lead-company-input" className="pg-input !py-2.5" value={form.company} onChange={h("company")} /></div>
            <div><label className="pg-label">Email</label><input data-testid="lead-email-input" className="pg-input !py-2.5" value={form.email} onChange={h("email")} /></div>
            <div><label className="pg-label">Phone</label><input data-testid="lead-phone-input" className="pg-input !py-2.5" value={form.phone} onChange={h("phone")} /></div>
            <div><label className="pg-label">Industry</label><input data-testid="lead-industry-input" className="pg-input !py-2.5" value={form.industry} onChange={h("industry")} /></div>
            <div><label className="pg-label">Est. Value</label><input data-testid="lead-value-input" className="pg-input !py-2.5" value={form.value} onChange={h("value")} placeholder="$" /></div>
            <div className="sm:col-span-3"><label className="pg-label">Notes</label><input data-testid="lead-notes-input" className="pg-input !py-2.5" value={form.notes} onChange={h("notes")} /></div>
          </div>
          <button data-testid="lead-save-button" onClick={addLead} className="btn-primary mt-4 !py-2.5 !px-5 text-[13px]">Save Lead</button>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="crm-error-state">
            <p className="text-red-600 text-[14px]">Failed to load leads.</p>
            <button data-testid="crm-retry-button" onClick={load} className="btn-primary mt-3 !py-2 !px-4 text-[12.5px]">Retry</button>
          </div>
        )}
        {!error && leads.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400" data-testid="crm-empty-state">No leads yet.</div>}
        {leads.map((l) => (
          <div key={l.id} className="bg-white rounded-xl border border-slate-200" data-testid={`lead-row-${l.id}`}>
            <div className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-[200px]">
                <div className="font-[Poppins] font-semibold text-[15px] text-[var(--pg-navy)]">{l.name} {l.company && <span className="text-slate-400 font-normal">· {l.company}</span>}</div>
                <div className="text-[12.5px] text-slate-500">{[l.email, l.phone, l.industry].filter(Boolean).join(" · ")}{l.source === "website_rfq" && <span className="ml-2 text-[10.5px] font-[Poppins] font-bold text-[var(--pg-accent)] uppercase">From RFQ</span>}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${STAGE_COLORS[l.stage] || "bg-slate-100"}`}>{l.stage.replace(/_/g, " ")}</span>
                <select data-testid={`lead-stage-select-${l.id}`} className="pg-input !py-2 !px-2 !w-auto text-[12.5px]" value={l.stage} onChange={(e) => setLeadStage(l.id, e.target.value)}>
                  {STAGES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
                <button data-testid={`lead-expand-${l.id}`} onClick={() => setExpanded(expanded === l.id ? null : l.id)} className="text-slate-400 hover:text-[var(--pg-navy)] p-1.5" aria-label="Activities">
                  {expanded === l.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <button data-testid={`lead-delete-${l.id}`} onClick={() => remove(l.id)} className="text-slate-400 hover:text-red-500 p-1.5" aria-label="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
            {expanded === l.id && <Activities leadId={l.id} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Activities({ leadId }) {
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [type, setType] = useState("note");
  const [due, setDue] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/leads/${leadId}`).then((r) => setItems(r.data.activities || [])).catch(() => {});
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!content.trim()) return;
    try {
      await api.post(`/admin/leads/${leadId}/activities`, { type, content, due_date: due });
      setContent(""); setDue(""); load();
    } catch { toast.error("Failed"); }
  };

  const toggle = async (a) => {
    try { await api.patch(`/admin/activities/${a.id}`, { done: !a.done }); load(); }
    catch (err) { console.error("Failed to toggle activity:", err); toast.error("Could not update the task"); }
  };

  return (
    <div className="border-t border-slate-100 p-4 bg-slate-50 rounded-b-xl" data-testid={`lead-activities-${leadId}`}>
      <div className="flex flex-wrap gap-2">
        <select data-testid="activity-type-select" className="pg-input !py-2 !w-auto text-[12.5px]" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="note">Note</option><option value="task">Task</option><option value="call">Call</option><option value="email">Email</option>
        </select>
        <input data-testid="activity-content-input" className="pg-input !py-2 flex-1 min-w-[180px] text-[13px]" placeholder="Add activity or follow-up..." value={content} onChange={(e) => setContent(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        {type === "task" && <input data-testid="activity-due-input" type="date" className="pg-input !py-2 !w-auto text-[13px]" value={due} onChange={(e) => setDue(e.target.value)} />}
        <button data-testid="activity-add-button" onClick={add} className="btn-primary !py-2 !px-3.5 text-[12.5px]"><Plus size={14} /></button>
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((a) => (
          <li key={a.id} className="flex items-start gap-2.5 text-[13px] bg-white rounded-lg px-3 py-2 border border-slate-100">
            {a.type === "task" && <input type="checkbox" checked={a.done} onChange={() => toggle(a)} className="mt-0.5" data-testid={`activity-done-${a.id}`} />}
            <div className="flex-1">
              <span className={a.done ? "line-through text-slate-400" : "text-[var(--pg-navy)]"}>{a.content}</span>
              <span className="ml-2 text-[11px] uppercase font-[Poppins] font-semibold text-slate-400">{a.type}{a.due_date && ` · due ${a.due_date}`}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
